// PHA-626 round 3: the clinician brief shown above the onboarding script. It is written for the
// physician / care team (never read to the patient) by the Python backend's LLM route
// (GET /api/patients/:id/clinician-brief via the BFF), from the chart and recent engagement.
//
// The phase follows the chart's own onboarding rule (needsContinueOnboarding / PHA-1073): only
// an APPROVED care plan means onboarded; a draft is work in progress.

export type ClinicianBriefPhase = 'needs_onboarding_no_plan' | 'needs_onboarding_draft' | 'onboarded';

export interface ClinicianBrief {
  phase: ClinicianBriefPhase;
  /** e.g. "Needs onboarding · draft care plan". */
  phase_label: string;
  /** Two or three sentences about who the patient is. */
  overview: string;
  /** No care plan yet: the 2-3 chronic conditions the AI suggests focusing on. */
  focus_conditions: { condition: string; reason?: string }[];
  medications_to_watch: { name: string; reason?: string }[];
  /** Draft or approved plan: high-level bullets about the plan. */
  care_plan: string[];
  /** Onboarded only: high-level engagement bullets over engagement_window_months. */
  engagement: string[];
  engagement_window_months: number;
  /** Onboarded only: when the patient joined the program (ISO date), and where it came from. */
  program_start_date: string | null;
  program_start_source: 'consent' | 'care_plan' | null;
  /** First month (YYYY-MM) the engagement bullets cover; never before the patient joined. */
  engagement_since: string | null;
}

export const CLINICIAN_BRIEF_PHASE_COLORS: Record<ClinicianBriefPhase, 'orange' | 'amber' | 'green'> = {
  needs_onboarding_no_plan: 'orange',
  needs_onboarding_draft: 'amber',
  onboarded: 'green',
};

/** Fills missing lists so the dialog can render any well-formed-enough response. */
export function normalizeClinicianBrief(raw: any): ClinicianBrief {
  const arr = (v: unknown) => (Array.isArray(v) ? v : []);
  const str = (v: unknown) => (typeof v === 'string' ? v.trim() : '');
  const phase: ClinicianBriefPhase =
    raw?.phase === 'onboarded' || raw?.phase === 'needs_onboarding_draft' ? raw.phase : 'needs_onboarding_no_plan';
  return {
    phase,
    phase_label: str(raw?.phase_label) || phase,
    overview: str(raw?.overview),
    focus_conditions: arr(raw?.focus_conditions)
      .map((f: any) => ({ condition: str(f?.condition), reason: str(f?.reason) || undefined }))
      .filter((f) => f.condition),
    medications_to_watch: arr(raw?.medications_to_watch)
      .map((m: any) => ({ name: str(m?.name), reason: str(m?.reason) || undefined }))
      .filter((m) => m.name),
    care_plan: arr(raw?.care_plan).map(str).filter(Boolean),
    engagement: arr(raw?.engagement).map(str).filter(Boolean),
    engagement_window_months: Number(raw?.engagement_window_months) || 3,
    program_start_date: /^\d{4}-\d{2}-\d{2}$/.test(str(raw?.program_start_date)) ? str(raw?.program_start_date) : null,
    program_start_source:
      raw?.program_start_source === 'consent' || raw?.program_start_source === 'care_plan' ? raw.program_start_source : null,
    engagement_since: /^\d{4}-\d{2}$/.test(str(raw?.engagement_since)) ? str(raw?.engagement_since) : null,
  };
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "2026-10-27" -> "Oct 27, 2026" (no Date(): an ISO date string would shift by time zone). */
export function formatBriefDate(iso: string | null): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso ?? '');
  return m ? `${MONTHS[Number(m[2]) - 1]} ${Number(m[3])}, ${m[1]}` : '';
}

/**
 * The engagement list's title. When the patient joined inside the window, the list starts at
 * the join month, so it says "since joining" rather than "past 3 months".
 */
export function engagementTitle(brief: ClinicianBrief): string {
  if (brief.program_start_date && brief.engagement_since && brief.program_start_date.slice(0, 7) === brief.engagement_since) {
    return `Engagement since joining (${formatBriefDate(brief.program_start_date)})`;
  }
  return `Engagement, past ${brief.engagement_window_months} months`;
}
